import React, { useEffect, useState } from 'react';
import { PrismButton } from '@prism-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faChevronDown, faChevronUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  COPILOT_URL,
  ESSAY_AI_PROMPT_DEFAULT,
  copyToClipboard,
  fetchEssayAiPrompt,
} from '../../../utils/essayAiPrompt';
import './EssayAiCoach.css';

type CopyState = 'idle' | 'copied' | 'failed';

/**
 * Offers the applicant a curated interview prompt to take to their own AI tool.
 *
 * The prompt instructs the AI to interview rather than to draft, so the applicant comes
 * back with their own material. Nothing here writes to the application — this is an aid,
 * and the essay is still typed by hand into the editor below.
 */
const EssayAiCoach: React.FC = () => {
  const [markdown, setMarkdown] = useState(ESSAY_AI_PROMPT_DEFAULT);
  const [enabled, setEnabled] = useState(true);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchEssayAiPrompt()
      .then((next) => {
        if (cancelled) return;
        setMarkdown(next.markdown);
        setEnabled(next.enabled);
      })
      .catch(() => {
        /* Keep the built-in default prompt. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (copyState !== 'copied') return undefined;
    const timer = setTimeout(() => setCopyState('idle'), 4000);
    return () => clearTimeout(timer);
  }, [copyState]);

  const handleCopy = async () => {
    const copied = await copyToClipboard(markdown);
    if (!copied) {
      // Reveal the prompt so the applicant can select it by hand rather than being told
      // something is on a clipboard that it never reached.
      setCopyState('failed');
      setShowPrompt(true);
      return;
    }
    setCopyState('copied');
    window.open(COPILOT_URL, '_blank', 'noopener,noreferrer');
  };

  if (!enabled) return null;

  return (
    <section className="essay-ai-coach" aria-labelledby="essay-ai-coach-heading">
      <h3 id="essay-ai-coach-heading" className="essay-ai-coach__heading">
        Stuck for a starting point? Let AI interview you.
      </h3>
      <p className="essay-ai-coach__body">
        We are not asking you to avoid AI — we are asking you to use it well. Copy the
        prompt below into Copilot (or any AI assistant). It will ask you questions about
        your own experience and will refuse to write the statement for you. Work through
        the questions in your own time, then come back and write your statement here from
        the notes it gives you.
      </p>

      <div className="essay-ai-coach__actions">
        <PrismButton tone="primary" size="sm" onPrismClick={handleCopy}>
          <FontAwesomeIcon icon={copyState === 'copied' ? faCheck : faCopy} />
          <span>
            {copyState === 'copied' ? 'Copied — opening Copilot' : 'Copy prompt & open Copilot'}
          </span>
        </PrismButton>
        <PrismButton
          display="outline"
          tone="secondary"
          size="sm"
          setAriaExpanded={showPrompt}
          onPrismClick={() => setShowPrompt((prev) => !prev)}
        >
          <FontAwesomeIcon icon={showPrompt ? faChevronUp : faChevronDown} />
          <span>{showPrompt ? 'Hide the prompt' : 'Read the prompt'}</span>
        </PrismButton>
      </div>

      {copyState === 'failed' && (
        <p className="essay-ai-coach__error" role="alert">
          Your browser blocked the clipboard. The prompt is shown below — select it and copy
          it manually.
        </p>
      )}

      {showPrompt && <pre className="essay-ai-coach__prompt">{markdown}</pre>}
    </section>
  );
};

export default EssayAiCoach;
